<template>
  <div class="container py-3">
    <!-- Header Section with Responsive Layout -->
    <div class="header-section mb-3 card-base theme-transition">
      <div class=" flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
        <div class="table-info mb-2 mb-md-0">
          <h5 class="ref mb-1" v-if="tab.Name">
            <strong v-if="isQuote">REF : </strong>
            {{ tab.Name.toUpperCase() }}
            <span class="badge tip-badge ms-1" v-if="tip > 0">Tip: {{ $utils.formatMoney(tip) }}</span>
          </h5>
          <h6 class="ref mb-0" v-if="isQuote && tab.Name">
            <strong>Quote No. {{ tab.saleNumber }}</strong>
          </h6>
          <div class="table-meta mt-2">
            <span class="meta-chip">Sale #{{ saleNumber }}</span>
            <span class="meta-chip" :class="isOffline ? 'meta-offline' : 'meta-online'">
              {{ isOffline ? 'Offline' : 'Online' }}
            </span>
            <span class="meta-chip">User: {{ assignedUser }}</span>
            <span class="meta-chip">Created: {{ createdDate }}</span>
            <span v-if="isShopOrder" class="meta-chip meta-shop">shop.co.zm</span>
          </div>
        </div>
        <div class="action-buttons align-item-md-left mt-2 mt-md-0 ml-2">
          <button class="btn btn-primary w-100" @click="updateTable" :disabled="isPayDisabled" v-if="!isOrder">
            <font-awesome-icon icon="save" class="me-1" />
            Save</button>
          <button class="btn btn-primary w-100" @click="pay" :disabled="isPayDisabled">
            <font-awesome-icon icon="money-bill-wave" class="me-1" />
            Pay</button>

          <button class="btn btn-primary w-100" @click="checkSales" v-if="!isOrder">
            <font-awesome-icon icon="door-open" class="me-1" />
            Open Table
          </button>


          <button class="btn btn-info w-100" @click="printBillSlipAndroid" v-if=" androidMode && !isOrder">
            <font-awesome-icon icon="receipt" class="me-1" />
            Bill Slip
          </button>

          <button class="btn btn-info w-100" @click="printBillSlip" v-if="!androidMode && !isOrder">
            <font-awesome-icon icon="receipt" class="me-1" />
            Bill Slip
          </button>
        </div>
      </div>
    </div>

    <!-- Selection Mode Banner -->
    <div class="selection-mode-banner card-base theme-transition" v-if="selectionMode && !isOrder">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <span class="selected-count">{{ selectedItems.length }} items selected</span>
        </div>
        <div class="selection-actions">
          <button class="btn btn-sm btn-outline-primary me-2" @click="selectAllItems">
            <font-awesome-icon icon="check-double" class="me-1" />
            Select All
          </button>
          <button class="btn btn-sm btn-outline-secondary me-2" @click="clearSelection">
            <font-awesome-icon icon="xmark" class="me-1" />
            Cancel
          </button>
          <button
            class="btn btn-sm btn-primary"
            @click="showTransferModal"
            :disabled="selectedItems.length === 0 || !isOnline"
          >
            <font-awesome-icon icon="right-left" class="me-1" />
            Transfer
          </button>
        </div>
      </div>
    </div>

    <!-- Toggle Selection Mode Button -->
    <div class="d-flex justify-content-end mb-2" v-if="!selectionMode && !isOrder">
      <button class="btn btn-outline-primary btn-sm" @click="toggleSelectionMode" :disabled="!isOnline">
        <font-awesome-icon icon="check-square" class="me-1" />
        Select Items
      </button>
    </div>

    <!-- Search Bar when in Selection Mode -->
    <div class="mb-3" v-if="selectionMode && !isOrder">
      <div class="input-group">
        <span class="input-group-text">
          <font-awesome-icon icon="search" />
        </span>
        <input
          type="text"
          class="form-control"
          placeholder="Search items..."
          v-model="searchQuery"
          @input="filterItems"
        >
      </div>
    </div>

    <!-- Items List with Better Spacing -->
    <div class="content pb-5 theme-transition">
      <div class="items-grid">
        <div
          class="card mb-2 card-base"
          v-for="(item, index) in filteredItems"
          :key="item.id || item.index || `${item.Plu || item.plu || item.PLU || 'item'}-${item.Description}-${index}`"
          @click="handleItemClick(item)"
          :class="{
            'selected': selectedItems.includes(item),
            'selectable': selectionMode && !isOrder
          }"
        >
          <div class="card-body p-2">
            <div class="item-header d-flex justify-content-between align-items-center mb-1">
              <div class="d-flex align-items-center pe-2" style="max-width: 70%;">
                <div class="selection-checkbox me-2" v-if="selectionMode && !isOrder">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="selectedItems.includes(item)"
                    @click.stop
                  >
                </div>
                <h6 class="mb-0 text-truncate">
                  {{ $utils.toPascalCase($utils.cutLength(item.Description, 30)) }}
                </h6>
              </div>
              <div class="item-total fw-bold">{{ $utils.formatMoney(item.Line) }}</div>
            </div>

            <div class="item-details">
              <div class="d-flex justify-content-between mb-1 align-items-center">
                <span class="sum-line">{{ item.Qty }} × {{ $utils.formatMoney(item.Unit) }}</span>
                <div class="d-flex align-items-center gap-2">
                  <span v-if="isOrder" class="badge bg-secondary">
                    <font-awesome-icon icon="fa-solid fa-lock" class="me-1" />
                    Locked
                  </span>
                  <TablesSaleQty v-if="!isOrder" @viewInstructions="viewInstructions" @void-item="removeItem" :sale-number="tab.saleNumber" :saleLine="item" :ref="el => setLineRef(el, index)" />
                </div>
              </div>

              <ul class="cooking-instruction-list ps-2 mb-0" v-if="item.instructions && item.instructions.length">
                <li class="cooking-instruction-list-item" v-for="instruction in item.instructions"
                    :key="instruction.CookingMsg">
                  <div class="d-flex justify-content-between">
                    <span>{{ instruction.CookingMsg }}</span>
                    <span class="ms-2">{{ instruction.count }} × {{ $utils.formatMoney(instruction.Price) }}</span>
                  </div>
                  <div class="text-end">Total: {{ $utils.formatMoney(instruction.count * instruction.Price) }}</div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="bottom-action-bar card-base" :class="{ 'expanded': showBottomActions }" v-if="!isOrder">

      <div class="total-handle" @click="toggleBottomActions">
        <div class="d-flex justify-content-between align-items-center">
          <span class="total-label">Total:</span>
          <span class="total-amount">{{ $utils.formatMoney(tabTotal() + tip) }}</span>
          <font-awesome-icon :icon="showBottomActions ? 'chevron-down' : 'chevron-up'" class="ms-2" />
        </div>
      </div>


      <div class="action-content" v-show="showBottomActions">
        <div class="action-buttons-grid">
          <button class="btn btn-info" @click="printBillSlip" v-if="isQuote &&!androidMode">
            <font-awesome-icon icon="receipt" class="me-1" />
            {{ (settings.PosType === 'Retail') ? 'Proforma Invoice' : 'Quote' }}
          </button>

          <button class="btn btn-primary" @click="splitTable" :disabled="!isOnline">
            <font-awesome-icon icon="arrows-split-up-and-left" class="me-1" />
            Split
          </button>
          <button class="btn btn-primary" @click="printProforma">
            <font-awesome-icon icon="receipt"/>
            Proforma
          </button>

          <button class="btn btn-primary" data-bs-toggle="modal"
                  :disabled="!isOnline"
                  :data-bs-target="`#transferModal-${tab.saleNumber}`">
            <font-awesome-icon icon="right-left" class="me-1" />
            Transfer Table
          </button>

          <button class="btn btn-danger" @click="voidTable" :disabled="!isOnline">
            <font-awesome-icon icon="fa-solid fa-trash" class="me-1" />
            Void
          </button>
        </div>
      </div>
    </div>

    <!-- Transfer Modal -->
    <div class="modal fade" :id="`transferModal-${tab.saleNumber}`" tabindex="-1" aria-labelledby="transferModal"
         aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Assign Table To:</h5>
            <button type="button" class="btn-close" :id="`closeTransferModal-${tab.saleNumber}`" data-bs-dismiss="modal"
                    aria-label="Close"></button>
          </div>
          <form @submit.prevent="transferTable">
            <div class="modal-body">
              <div class="mb-3">
                <label for="usersList" class="form-label">Select Waiter</label>
                <v-select id="usersList" v-model="assignUser" :options="users">
                </v-select>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
              <button type="submit" class="btn btn-primary" :disabled="!assignUser">Submit</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Item Transfer Modal -->
    <div class="modal" id="itemTransferModal" tabindex="-1" aria-labelledby="itemTransferModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="itemTransferModalLabel">
              Transfer {{ selectedItems.length }} Items
            </h5>
            <button type="button" class="btn-close" @click="clearSelection" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="selected-items-summary mb-3" v-if="selectedItems.length > 0">
              <h6 class="mb-2">Selected Items:</h6>
              <div class="selected-items-list">
                <div class="selected-item" v-for="(item, idx) in selectedItems.slice(0, 3)" :key="idx">
                  <span class="item-name">{{ $utils.cutLength(item.Description, 30) }}</span>
                  <span class="item-price">{{ $utils.formatMoney(item.Line) }}</span>
                </div>
                <div class="more-items" v-if="selectedItems.length > 3">
                  <span>+ {{ selectedItems.length - 3 }} more items</span>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label for="tablesList" class="form-label">Select Target Table</label>
              <v-select
                id="tablesList"
                v-model="selectedTable"
                :options="availableTablesForSelection"
                label="Name"
                track-by="saleNumber"
                :filterable="true"
                placeholder="Search tables..."
              ></v-select>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="clearSelection">Cancel</button>
            <button
              type="button"
              class="btn btn-primary"
              @click="confirmTransfer"
              :disabled="!selectedTable || selectedItems.length === 0 || !isOnline"
            >
              Transfer Items
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Storage } from "@/services/Storage";
import TablesSaleQty from "@/components/point-of-sale/sales/TablesSaleQty.vue";
import connectivityMonitor from "@/services/connectivityMonitor";
import storeOrdersService from "@/services/storeOrdersService";
import { Modal } from 'bootstrap';
// Ticket #26425 — empty save / name omit guards on sheet Save & Pay
// docs/design-docs/2026-08-01-ticket-26425-client-merge-investigation.md
import {
  EMPTY_TABLE_SYNC_BLOCKED_MSG,
  resolveConfirmedNameFromResponse,
  shouldBlockSavePayEmptyExisting,
} from "@/services/tableSyncGuards";
import { responses } from "@/utils/responses";
// docs/FRONTEND.md — sale line Qty contract; qty-reset-on-save loss vector #3
import { normalizeSaleLines } from "@/utils/saleLineQty";
import {
  faSave,
  faMoneyBillWave,
  faDoorOpen,
  faChevronUp,
  faChevronDown,
  faCheckSquare,
  faCheckDouble,
  faXmark,
  faSearch,
  faRightLeft
} from '@fortawesome/free-solid-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';

library.add(
  faSave,
  faMoneyBillWave,
  faDoorOpen,
  faChevronUp,
  faChevronDown,
  faCheckSquare,
  faCheckDouble,
  faXmark,
  faSearch,
  faRightLeft
);

export default {
  name: "TableDetails",
  components: { TablesSaleQty },
  beforeMount() {
    this.getTipTotal()
  },
  created() {
    this.isOnline = connectivityMonitor.getStatus();
    this._unsubOnline = connectivityMonitor.subscribe('online', () => this.isOnline = true);
    this._unsubOffline = connectivityMonitor.subscribe('offline', () => this.isOnline = false);
  },
  mounted() {
    // All Tables list lines lack currentsale id/saleline; hydrate before void.
    // docs: FRONTEND.md cart line identity; ticket 22844 All Tables follow-up
    this.hydrateLineIdentities();
  },
  beforeDestroy() {
    this._unsubOnline && this._unsubOnline();
    this._unsubOffline && this._unsubOffline();
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  props: {
    tab: { type: Object, required: true },
  },
  data() {
    return {
      loading: false,
      tip: 0,
      assignUser: '',
      showBottomActions: false,
      isOnline: true,
      allTables: [],
      selectedTable: null,
      selectedItems: [],
      itemTransferModal: null,
      selectionMode: false,
      searchQuery: '',
      filteredItems: [],
      // Component refs for each rendered TablesSaleQty line. Populated via
      // :ref="el => setLineRef(el, index)" so the parent can invoke
      // voidItem() on a specific line (mirrors NewShoppingChart.lineRefs).
      lineRefs: {},
      /**
       * Server-loaded table name before any local edit.
       * Used as previousName on save so rename can send OLD vs NEW correctly
       * (previousName must not equal the edited Name or Name is stripped).
       * docs: src/services/tableSyncGuards.js resolveNameForSyncPayload
       */
      originalName: null,
      /** Sale key for which originalName was captured (re-capture on table change). */
      _originalNameSaleKey: null,
    }
  },
  computed: {
    isQuote() {
      return !!(this.tab.customer && this.tab.customer !== '');
    },
    hasInstructions() {
      return (this.tab.instructions && this.tab.instructions.length > 0)
    },
    isOrder() {
      return this.tab.Name && this.tab.Name.toUpperCase().startsWith('SHOP.CO.ZM ORDER');
    },
    isShopOrder() {
      return (this.tab.Name || '').toUpperCase().startsWith('SHOP.CO.ZM ORDER');
    },
    saleNumber() {
      return this.tab.saleNumber || this.tab.SaleNum || 'N/A';
    },
    assignedUser() {
      return this.tab.user || this.tab.User || 'Unassigned';
    },
    isOffline() {
      if (!this.tab) return false;
      const synced = this.tab.synced;
      if (synced === false || synced === 0 || synced === '0' || synced === 'false') return true;
      if (this.tab.pendingSync === true || this.tab.isOffline === true || this.tab.offline === true) return true;
      const name = String(this.tab.Name || '');
      if (name.endsWith('_offline')) return true;
      return false;
    },
    createdDate() {
      const raw = this.tab.createdAt || this.tab.CreatedAt || this.tab.createdDate || this.tab.CreatedDate ||
        this.tab.created || this.tab.DateCreated || this.tab.date || this.tab.Date || this.tab.created_on || this.tab.createdOn;
      return this.formatDate(raw);
    },
    users() {
      const names = this.$store.getters.getUsers.map(user => user.Username).filter(Boolean);
      return Array.from(new Set(names));
    },
    settings() {
      return this.$store.getters.getDeviceSettings;
    },
    androidMode() {
      return this.$store.getters.getDeviceSettings?.androidMode || false;
    },
    /**
     * Disable Save/Pay when lines are invalid OR when an existing sale has no items
     * (Ticket #26425 Fix D — never pay an unhydrated merged sale).
     * @returns {boolean}
     */
    isPayDisabled() {
      const items = this.tab?.items;
      if (!items || !Array.isArray(items) || items.length === 0) {
        // Existing open table with zero local lines must not Save/Pay until hydrated
        const sn = Number(this.tab?.saleNumber || this.tab?.SaleNum || 0);
        if (Number.isFinite(sn) && sn > 0) return true;
        return true;
      }
      return items.some(item => item.Qty <= 0 || isNaN(item.Qty));
    },
    availableTablesForSelection() {
      return this.allTables.map(table => ({
        ...table,
        Name: table.Name || `Table ${table.saleNumber}`,
      }));
    }
  },
  watch: {
    tab: {
      handler(newTab) {
        if (newTab) {
          // Capture original server name once per table identity (before local edits).
          // docs: tableSyncGuards — previousName must be pre-edit server name
          const sn = newTab.saleNumber ?? newTab.SaleNum ?? null;
          const saleKey = sn != null ? String(sn) : '__none__';
          if (this._originalNameSaleKey !== saleKey) {
            this._originalNameSaleKey = saleKey;
            this.originalName = newTab.Name || newTab.name || null;
            // New table card from All Tables list — hydrate line ids for void.
            this.hydrateLineIdentities();
          }
        }
        if (newTab && newTab.items) {
          newTab.items.forEach(item => {
            if (typeof item.savedQty === 'undefined') {
              const qty = item.Qty || item.qty || item.Quantity || item.quantity || 0;
              item.savedQty = Number(qty);
              console.log(`[TableDetails] Watcher: Initialized savedQty for ${item.Description} to ${item.savedQty}`);
            }
          });
        }
      },
      deep: true,
      immediate: true
    },
    'tab.items': {
      immediate: true,
      handler(newItems) {
        this.filteredItems = newItems || [];
      }
    }
  },
  methods: {
    formatDate(value) {
      if (!value) return 'N/A';
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return 'N/A';
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    viewInstructions(plu) {
      return this.updateTable()
          .then(() => {
            this.$store.dispatch(`openTable`, this.tab)
            return this.$router.push({
              name: 'instruction',
              query: { plu }
            })
          })
    },
    /**
     * Merge stable currentsale id/saleline from savetable detail into
     * tab.items. getAllTables returns display-only lines (Plu/Qty/Line)
     * without PK fields; BE logItemVoid requires id to archive the row.
     * Safe no-op when offline or when lines already carry ids.
     * Does not open the table into the cart (pay/edit paths still use openTable).
     *
     * @returns {Promise<void>}
     */
    async hydrateLineIdentities() {
      if (!this.tab || !Array.isArray(this.tab.items) || this.tab.items.length === 0) return;
      const sn = Number(this.tab.saleNumber || this.tab.SaleNum || 0);
      if (!Number.isFinite(sn) || sn <= 0) return;

      // Skip network when every line already has a stable id.
      const needsHydrate = this.tab.items.some(
        (item) => !(item.id || item.Id || item.lineId || item.currentSaleLineId)
      );
      if (!needsHydrate) return;
      if (!this.isOnline) return;

      try {
        const detail = await this.$store.dispatch('getTableData', sn);
        const detailItems = detail?.items || detail?.Items || [];
        if (!Array.isArray(detailItems) || detailItems.length === 0) return;

        // Index detail lines by PLU then description for multi-same-PLU tables.
        const byKey = new Map();
        detailItems.forEach((line, idx) => {
          const plu = String(line.Plu || line.plu || '');
          const desc = String(line.Description || line.description || '');
          const keyExact = `${plu}::${desc}`;
          if (!byKey.has(keyExact)) byKey.set(keyExact, []);
          byKey.get(keyExact).push(line);
          const keyPlu = `${plu}::`;
          if (!byKey.has(keyPlu)) byKey.set(keyPlu, []);
          byKey.get(keyPlu).push(line);
          // Preserve order fallback by index when PLU+desc collide.
          byKey.set(`idx::${idx}`, line);
        });

        this.tab.items.forEach((item, idx) => {
          if (item.id || item.Id || item.lineId || item.currentSaleLineId) return;
          const plu = String(item.Plu || item.plu || '');
          const desc = String(item.Description || item.description || '');
          const exactBucket = byKey.get(`${plu}::${desc}`) || [];
          const pluBucket = byKey.get(`${plu}::`) || [];
          const match = exactBucket.shift() || pluBucket.shift() || byKey.get(`idx::${idx}`);
          if (!match) return;
          const id = match.id || match.Id || match.lineId || match.currentSaleLineId || null;
          const saleline = match.saleline != null
            ? match.saleline
            : (match.SaleLine != null ? match.SaleLine : (match.saleLine != null ? match.saleLine : null));
          if (id) {
            this.$set ? this.$set(item, 'id', id) : (item.id = id);
          }
          if (saleline != null && item.saleline == null) {
            this.$set ? this.$set(item, 'saleline', saleline) : (item.saleline = saleline);
          }
        });
      } catch (err) {
        console.warn('[TableDetails] hydrateLineIdentities failed (void will retry on demand)', err);
      }
    },

    /**
     * Handles a void request from the TablesSaleQty child component
     * (bound to the @void-item event). Always removes the sale line from
     * tab.items when emitted — TablesSaleQty already applied the
     * salenum-aware gate (#26827 unsaved always-emit vs 22844 saved
     * success-only). Recalculates remaining line totals and persists via
     * updateTable → AddOrUpdateTable.
     *
     * Offline is allowed for line voids (TicketApp #26827 Option A):
     * table shape is persisted through the existing table offline path
     * (outbox_table_ops / tableServerClient). Do not hard-block here —
     * that left cashiers stuck with "You must be online to void".
     * Whole-table void stays online-only (voidTable hard-block).
     * Idempotent: second emit for the same line is a no-op once spliced.
     * docs: FRONTEND.md cart line identity; services/voidOutbox.js
     *
     * @param {Object} saleLine - The sale line to remove
     */
    async removeItem(saleLine) {
      if (!saleLine || !this.tab || !Array.isArray(this.tab.items)) return;
      // Prefer stable currentsale id when present so multi-same-PLU tables
      // remove the voided line, not the first PLU match.
      const voidId = saleLine.id || saleLine.Id || saleLine.lineId || saleLine.currentSaleLineId;
      let idx = -1;
      if (voidId) {
        idx = this.tab.items.findIndex((item) => {
          const itemId = item.id || item.Id || item.lineId || item.currentSaleLineId;
          return itemId && String(itemId) === String(voidId);
        });
      }
      if (idx === -1) {
        idx = this.tab.items.findIndex(
          (item) => (item.Plu || item.plu) === (saleLine.Plu || saleLine.plu)
        );
      }
      // Already removed (double void) — no-op, do not re-persist or re-toast.
      if (idx === -1) {
        console.warn('[TableDetails] removeItem: line not found in tab.items (idempotent no-op)');
        return;
      }
      // Local remove is unconditional once TablesSaleQty emitted void-item.
      this.tab.items.splice(idx, 1);
      this.tab.items.forEach(item => {
        if (typeof item.Line === 'number' && typeof item.Qty === 'number' && typeof item.Unit === 'number') {
          item.Line = (item.Qty * item.Unit) - Number(item.Discount || 0);
        }
      });
      try {
        await this.updateTable({ suppressPrint: true });
        responses.showInfo('Item voided successfully');
        this.$emit('updateAction');
      } catch (err) {
        console.error('[TableDetails] removeItem failed to persist:', err);
      }
    },

    /**
     * Stores a reference to each rendered TablesSaleQty component so the
     * parent can invoke voidItem() on a specific line (mirrors the
     * setLineRef pattern used by NewShoppingChart).
     * @param {Vue} el - The component instance (or null when unmounted)
     * @param {number} index - The index of the line in tab.items
     */
    setLineRef(el, index) {
      if (el) {
        this.lineRefs[index] = el;
      } else if (this.lineRefs) {
        delete this.lineRefs[index];
      }
    },
    async pay() {
      console.log('[TableDetails][pay] Starting payment flow, tab.saleNumber:', this.tab.saleNumber, 'tab.SaleNum:', this.tab.SaleNum);
      console.log('[TableDetails][pay] Calling updateTable with suppressPrint: true');
      try {
        const result = await this.updateTable({ suppressPrint: true });
        console.log('[TableDetails][pay] updateTable completed, result:', result);
        console.log('[TableDetails][pay] After updateTable, currentSale:', this.$store.getters.getCurrentSale);
      } catch (err) {
        console.error('[TableDetails][pay] updateTable failed:', err);
        return false;
      }

      await this.$store.dispatch(`openTable`, this.tab)
      console.log('[TableDetails][pay] After openTable, currentSale:', this.$store.getters.getCurrentSale, 'openTableName:', this.$store.getters['getOpenTableName']);

      if (this.isOrder) {
        try {
          this.$utils.showLoading();
          const store = await Storage.getDefaultStore();
          const response = await storeOrdersService.getSale(store.StoreDB, this.tab.saleNumber);

          if (response && response.status === 'SUCCESS' && response.data && response.data.payment) {
            const payment = response.data.payment;
            if (payment.status === 'PAID' && payment.method && payment.method.toUpperCase().includes('KAZANG')) {
              const total = this.tabTotal() + this.tip;

              const payload = {
                refund: false,
                total: Number(total.toFixed(2)),
                paid: Number(total.toFixed(2)),
                SecondRefrence: payment.paymentReference,
                change: 0,
                type: "Kazang",
                saleNumber: this.tab.saleNumber || this.tab.SaleNum
              };

              await this.$store.dispatch('processTransaction', payload);
              this.$utils.hideLoading();
              return;
            }
          }
        } catch (err) {
          console.error('Error checking online payment status', err);
        } finally {
          this.$utils.hideLoading();
        }
      }

      return this.$router.push({
        name: 'payment',
        query: { saleNumber: this.tab.saleNumber || this.tab.SaleNum }
      })
    },

    /**
     * Persist the open table sheet to the server / Main PC table server.
     * Ticket #26425 Fix A/C/D:
     *  - Block Save/Pay when SaleNum>0 and local items are empty (wipe vector).
     *  - Stamp command:"save" and previousName so Name is omitted when unchanged.
     *  - Apply server-confirmed Name from the response (server is authoritative).
     * docs/design-docs/2026-08-01-ticket-26425-client-merge-investigation.md
     *
     * @param {{ suppressPrint?: boolean }} [options]
     * @returns {Promise<object>}
     */
    async updateTable(options = {}) {
      // Keep Line math on authoritative local tab.items (pre-existing).
      // B5b (PLAN) / B5 (SPEC): wire payload clones + strips existing-instruction
      // count/qty so repeated Pay does not re-submit historical multipliers.
      // Does NOT mutate local instruction state or savedQty print bookkeeping.
      // docs: Lytning/SPEC.md Task B5; Lytning/PLAN.md B5b
      //
      // Ticket 22844 — await line-id hydrate before building the save payload.
      // Mount/watcher fire-and-forget hydrate can still be in flight when the
      // waiter hits Save after a qty bump; missing ids caused AddOrUpdateTable
      // to mint fresh UUIDs → BE INSERT doubles. Await here so tab.items carry
      // currentsale ids when possible; AddOrUpdateTable also re-hydrates as a
      // second belt-and-braces guard.
      // docs: FRONTEND.md cart line identity
      try {
        await this.hydrateLineIdentities();
      } catch (hydrateErr) {
        console.warn(
          '[TableDetails][updateTable] hydrateLineIdentities before save failed',
          hydrateErr
        );
      }

      let localLines = Array.isArray(this.tab.items) ? this.tab.items : [];
      let settings = await Storage.getDeviceSettings();
      for (let i = 0; i < localLines.length; i++) {
        let line = localLines[i];
        localLines[i].Line = (Number(line.Qty) * Number(line.Unit)) - Number(line.Discount);
      }
      const saleNum = this.tab.saleNumber || this.tab.SaleNum;

      // Wire-only clone: strip count/qty from server-known instructions
      const saleLines = localLines.map((item) => {
        if (!item || !Array.isArray(item.instructions) || item.instructions.length === 0) {
          return item;
        }
        const instructions = item.instructions.map((instr) => {
          if (!instr || typeof instr !== 'object') return instr;
          const hasServerIdentity =
            (instr.id != null && instr.id !== '') ||
            (instr.cookingNum != null && instr.cookingNum !== '') ||
            (instr.CookingNum != null && instr.CookingNum !== '');
          if (!hasServerIdentity) return instr;
          const clone = { ...instr };
          delete clone.count;
          delete clone.qty;
          delete clone.Count;
          delete clone.Qty;
          return clone;
        });
        return { ...item, instructions };
      });

      // Fix A/D — sheet still dispatches when empty so AddOrUpdateTable can re-fetch
      // and hydrate server lines (preferred recovery). If server is also empty, action throws.
      if (shouldBlockSavePayEmptyExisting(saleNum, localLines)) {
        console.warn('[TableDetails][updateTable] empty local items on existing sale — dispatching for server re-fetch', {
          saleNum,
        });
      }

      // previousName = server-loaded original (not the current edit value).
      // When equal to Name, guard omits Name (Fix C). When different, rename:true
      // keeps Name on the wire so the backend actually renames the table.
      const previousName =
        this.originalName ||
        this.tab._originalName ||
        null;
      const currentName = this.tab.Name || this.tab.name || null;
      const nameChanged =
        !!previousName &&
        !!currentName &&
        String(previousName).toLowerCase() !== String(currentName).toLowerCase();

      let data = {
        SaleNum: saleNum,
        Name: currentName,
        User: this.tab.user || this.tab.User,
        PC: settings.PC,
        items: saleLines,
        Customer: this.tab.customer || this.tab.Customer,
        discount: parseFloat(this.$store.getters.getSaleDiscount.total),
        suppressPrint: options.suppressPrint === true,
        // Fix A — ordinary sheet save
        command: 'save',
        // Fix C / rename — OLD server name vs NEW typed name
        previousName,
        rename: nameChanged === true,
        expectedRevision: this.tab.revision ?? this.tab.expectedRevision ?? null,
      };

      this.loadPanel();
      try {
        const res = await this.$store.dispatch('AddOrUpdateTable', data);
        // Fix: Update local savedQty to match current Qty after successful save
        // This prevents redundant printing if "Pay" is clicked immediately after without changes
        if (this.tab && this.tab.items) {
          this.tab.items.forEach(item => {
            item.savedQty = Number(item.Qty);
          });
        }
        // Fix C — always adopt server-confirmed name when present
        const confirmed = resolveConfirmedNameFromResponse(
          res,
          this.tab?.Name || this.tab?.name
        );
        if (confirmed && this.tab) {
          this.$set
            ? this.$set(this.tab, 'Name', confirmed)
            : (this.tab.Name = confirmed);
          // Keep originalName aligned with server so next save omits unchanged Name
          this.originalName = confirmed;
        }
        // If AddOrUpdateTable hydrated items onto the payload result, reflect them.
        // Normalize qty→Qty / unit→Unit so post-save cart math never sees NaN
        // and the next save does not persist Qty=1 (qty-reset-on-save #3).
        const resItems = res?.items || res?.Items;
        if (Array.isArray(resItems) && resItems.length > 0 && this.tab) {
          this.tab.items = normalizeSaleLines(resItems);
          // Keep savedQty aligned with normalized Qty after hydrate
          this.tab.items.forEach((item) => {
            item.savedQty = Number(item.Qty);
          });
        }
        return res;
      } catch (err) {
        if (err?.code === 'EMPTY_SAVE_BLOCKED') {
          try {
            responses.showWarning(err.message || EMPTY_TABLE_SYNC_BLOCKED_MSG, 3500);
          } catch (_) { /* optional */ }
        } else {
          errorHandler.tomcatError(err);
        }
        throw err;
      } finally {
        Swal.close();
      }
    },
    loadPanel() {
      return Swal.fire({
        html: 'Updating table....',
        allowOutsideClick: false,
        timerProgressBar: true,
        didOpen: () => {
          Swal.showLoading()
        },
      });
    },
    async splitTable() {
      if (!this.isOnline) return;
      let isAllowed = await this.$store.dispatch(`validateSecurityPermission`, `splitTable`)
      if (!isAllowed) return;
      return this.$router.push({
        name: `split-table`,
        query: { saleNumber: this.tab.saleNumber }
      })
    },
    async printBillSlip() {
      this.$utils.showLoading();
      return this.$store.dispatch(`printBillSlip`, this.tab.saleNumber)
          .finally(() => this.$utils.hideLoading())
    },
    async printBillSlipAndroid() {
      this.$utils.showLoading();
      return this.$store.dispatch(`printBillSlipAndroid`, this.tab.saleNumber)
          .finally(() => this.$utils.hideLoading())
    },
    async printProforma() {
      this.$utils.showLoading()
      return this.$store.dispatch(`printProforma`, this.tab.saleNumber)
          .finally(() => this.$utils.hideLoading())
    },
    async getTipTotal() {
      let saleTotal = this.tabTotal();
      let settings = await Storage.getDeviceSettings();
      let tip = 0;
      if (settings && settings.AutoTip > 0) {
        tip = Number(settings.AutoTip);
        tip = (tip / 100) * saleTotal;
      }
      this.tip = tip;
    },
    tabTotal() {
      let sum = 0;
      let items = this.tab.items;
      if (!items) return 0.00;
      for (let i = 0; i < items.length; i++) {
        sum += items[i].Line
        let instructions = items[i].instructions || [];
        for (let k = 0; k < instructions.length; k++) {
          let price = instructions[k].Price;
          sum += price;
        }
      }
      return sum;
    },
    checkSales() {
      let sale = this.$store.getters.getSaleLines;
      if (sale.length > 0) {
        Swal.fire({
          title: 'Are you sure?',
          text: "Opening this table will override the current sale",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, Override it!'
        }).then((result) => {
          if (result.isConfirmed) {
            this.selectTab();
          }
        })
      } else {
        this.selectTab()
      }
    },
    async selectTab() {
      let isAllowed = await this.$store.dispatch('validateSecurityPermission', `LoadTable`)
      if (!isAllowed) return;
      const saleNumber = this.tab.saleNumber || this.tab.SaleNum;
      return this.$router.push({
        name: 'pos',
        query: { saleNumber }
      });
    },


    /**
     * Voids the entire open table by dispatching `removeTable` against the
     * table server. The user is asked to confirm before the action runs.
     *
     * Still blocked when offline (TicketApp #26827 scope): line-item voids
     * now queue via voidOutbox, but whole-table void has no dedicated
     * outbox path yet and remains online-only. The red "Void" button is
     * also visually disabled via `:disabled="!isOnline"`. Product decision
     * needed before enabling offline whole-table void.
     * docs: services/voidOutbox.js (line voids only)
     */
    async voidTable() {
      if (!this.isOnline) {
        return Swal.fire({
          icon: 'warning',
          title: 'You are offline',
          text: 'You must be online to void a table. Reconnect and try again.'
        });
      }
      return Swal.fire({
        title: 'Are you sure?',
        text: "This step cannot be reversed!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, Void it!',
        showLoaderOnConfirm: true,
        preConfirm: () => {
          return this.$store.dispatch('removeTable', this.tab)
              .finally(() => this.$emit('updateAction'))
        },
      }).then((result) => {
        if (result.isConfirmed) {
          return responses.showInfo(`Table Voided Successfully`)
        }
      })
    },

    transferTable() {
      if (!this.isOnline) return;
      document.getElementById(`closeTransferModal-${this.tab.saleNumber}`).click();
      let obj = {
        saleNumber: this.tab.saleNumber,
        transferredTo: this.assignUser,
      }
      return this.$store.dispatch(`transferTable`, obj)
          .then((res) => {
            if (res) return this.$emit('updateAction');
          })
    },
    toggleBottomActions() {
      this.showBottomActions = !this.showBottomActions;
    },
    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode;
      if (!this.selectionMode) {
        this.clearSelection();
      }
      this.filterItems();
    },
    handleItemClick(item) {
      if (!this.selectionMode || this.isOrder) return;
      this.toggleItemSelection(item);
    },
    toggleItemSelection(item) {
      const idx = this.selectedItems.indexOf(item);
      if (idx === -1) {
        this.selectedItems.push(item);
      } else {
        this.selectedItems.splice(idx, 1);
      }
    },
    selectAllItems() {
      if (this.filteredItems.length === this.selectedItems.length) {
        this.selectedItems = [];
      } else {
        this.selectedItems = [...this.filteredItems];
      }
    },
    clearSelection() {
      this.selectedItems = [];
      this.selectedTable = null;
      this.closeTransferModal();
    },
    async showTransferModal() {
      if (this.selectedItems.length === 0 || !this.isOnline) return;
      await this.loadAvailableTables();

      if (this.itemTransferModal) {
        this.itemTransferModal.dispose();
        this.itemTransferModal = null;
      }

      const modalElement = document.getElementById('itemTransferModal');
      this.itemTransferModal = new Modal(modalElement, {
        backdrop: 'static',
        keyboard: false
      });
      this.itemTransferModal.show();
    },
    filterItems() {
      if (!this.searchQuery.trim()) {
        this.filteredItems = this.tab.items || [];
        return;
      }
      const query = this.searchQuery.toLowerCase().trim();
      this.filteredItems = (this.tab.items || []).filter(item =>
        item.Description.toLowerCase().includes(query)
      );
    },
    async confirmTransfer() {
      if (!this.selectedTable || this.selectedItems.length === 0 || !this.isOnline) return;

      Swal.fire({
        title: 'Preparing Transfer...',
        text: 'Please wait while we process your request',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => Swal.showLoading()
      });

      try {
        await this.$store.dispatch('getAllTables');
        const payload = {
          fromSaleNumber: Number(this.tab.saleNumber),
          toSaleNumber: Number(this.selectedTable.saleNumber),
          items: this.selectedItems.map(item => ({
            ...item,
            Plu: item.Plu,
            Description: item.Description,
            Qty: Number(item.Qty),
            Unit: Number(item.Unit),
            Line: Number(item.Line)
          }))
        };

        await this.$store.dispatch('transferItems', payload);

        const targetTableName = this.selectedTable?.Name || 'target table';
        this.closeTransferModal();
        this.selectedItems = [];
        this.selectionMode = false;
        this.selectedTable = null;
        this.$emit('updateAction');

        Swal.fire({
          icon: 'success',
          title: 'Items Transferred',
          text: `Successfully transferred ${payload.items.length} items to ${targetTableName}`,
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Transfer error in component:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'There was an error transferring the items.';
        this.closeTransferModal();
        Swal.fire({
          icon: 'error',
          title: 'Transfer Failed',
          text: errorMessage,
          showConfirmButton: true
        });
      }
    },
    async loadAvailableTables() {
      try {
        // Use action to fetch directly from API, bypassing paidTablesOutbox filter
        const allTables = await this.$store.dispatch('getOpenTables') || [];
        this.allTables = allTables.filter(table =>
          Number(table.saleNumber) !== Number(this.tab.saleNumber)
        );
      } catch (error) {
        console.error('Error loading tables:', error);
        this.allTables = [];
      }
    },
    closeTransferModal() {
      try {
        if (this.itemTransferModal) {
          this.itemTransferModal.hide();
          setTimeout(() => {
            if (this.itemTransferModal) {
              this.itemTransferModal.dispose();
              this.itemTransferModal = null;
            }
          }, 300);
        }

        const modalElement = document.getElementById('itemTransferModal');
        if (modalElement) {
          modalElement.classList.remove('show');
          modalElement.style.display = 'none';
          modalElement.setAttribute('aria-hidden', 'true');
          modalElement.removeAttribute('aria-modal');
        }

        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) backdrop.remove();
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      } catch (error) {
        console.error('Error closing transfer modal:', error);
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) backdrop.remove();
      }
    },
    handleKeyDown(event) {
      if (event.key === ' ' && this.selectionMode && !this.isOrder) {
        event.preventDefault();
        this.showTransferModal();
      }
    }
  },
  async mounted() {
    await this.loadAvailableTables();
    this.filteredItems = this.tab.items || [];
    window.addEventListener('keydown', this.handleKeyDown);
  },
}
</script>

<style lang="scss" scoped>
// Main container styling
.container {
  background: var(--bg-primary);
  color: var(--text-primary);
}

// Header section styling
.header-section {
  position: sticky;
  top: 0;
  background: var(--card-bg);
  z-index: 10;
  padding: 15px 0;
  border-bottom: var(--card-border);
  box-shadow: var(--card-shadow);

  .table-info {
    padding: 0 10px;

    .ref {
      color: var(--text-primary);
      margin-bottom: 0.5rem;
    }
  }
}

.table-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0 10px;
}

.meta-chip {
  background: var(--bg-primary);
  color: var(--text-secondary);
  border: var(--card-border);
  border-radius: 999px;
  padding: 0.25rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.meta-online {
  background: rgba(50, 215, 75, 0.12);
  color: var(--success);
  border-color: rgba(50, 215, 75, 0.25);
}

.meta-offline {
  background: rgba(255, 59, 48, 0.12);
  color: var(--danger);
  border-color: rgba(255, 59, 48, 0.25);
}

.meta-shop {
  background: rgba(165, 41, 255, 0.12);
  color: var(--accent-neon-purple);
  border-color: rgba(165, 41, 255, 0.2);
}

.selection-mode-banner {
  background: rgba(30, 136, 229, 0.12);
  border: var(--card-border);
  padding: 10px 15px;
  border-radius: 8px;
  margin-bottom: 12px;
  box-shadow: var(--card-shadow);

  .selected-count {
    font-weight: 600;
    color: var(--text-primary);
  }

  .selection-actions {
    display: flex;
    align-items: center;
  }
}

// Action buttons improvements
.action-buttons {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 8px;
  padding: 0 10px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  }

  button {
    padding: 8px 12px;
    font-size: 0.85rem;
    height: 40px;
    border-radius: 8px;
    transition: all 0.2s ease;
    margin: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &:active {
      transform: translateY(1px);
    }

    .me-1 {
      margin-right: 8px;
    }
  }
}

// Content area improvements
.content {
  padding: 15px 0;
  margin-bottom: 60px; // Reduced margin for collapsed state
  background: var(--bg-primary);

  // Grid layout for items
  .items-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
    padding: 0 10px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 12px;
    }

    @media (min-width: 769px) and (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: 1025px) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .card {
    &.selectable {
      cursor: pointer;
      &:hover {
        background: rgba(30, 136, 229, 0.08);
      }
    }

    &.selected {
      border: 2px solid var(--accent-electric-blue);
      background: rgba(30, 136, 229, 0.14);
    }

    width: 100%;
    border-radius: 12px;
    border: var(--card-border);
    box-shadow: var(--card-shadow);
    background: var(--card-bg);
    transition: transform 0.2s ease;
    margin-bottom: 0;

    &:active {
      transform: scale(0.99);
    }

    .card-body {
      padding: 12px 16px;
      background: var(--card-bg);
    }

    .item-header {
      h6 {
        font-size: 1rem;
        color: var(--text-primary);
      }

      .item-total {
        color: var(--accent-electric-blue);
        font-size: 1.1rem;
      }
    }

    .item-details {
      .sum-line {
        color: var(--text-secondary);
        font-size: 0.9rem;
      }
    }
  }
}

// Updated Bottom Action Bar
.bottom-action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--card-bg);
  z-index: 100;
  transition: transform 0.3s ease-in-out;

  // Total Handle Styling
  .total-handle {
    padding: 12px 16px;
    background: var(--bg-primary);
    border-top: var(--card-border);
    box-shadow: var(--card-shadow);
    cursor: pointer;
    user-select: none;

    &:active {
      background: var(--secondary-button-bg);
    }

    .total-label {
      font-size: 1.1rem;
      color: var(--text-primary);
    }

    .total-amount {
      font-size: 1.3rem;
      color: var(--accent-electric-blue);
      font-weight: 700;
    }
  }

  // Action Content (sliding part)
  .action-content {
    background: var(--card-bg);
    padding: 16px;
    border-top: var(--card-border);
    box-shadow: var(--card-shadow);
  }

  // Action Buttons Grid
  .action-buttons-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 12px;
    width: 100%;

    @media (min-width: 768px) {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    }

    button {
      padding: 12px 16px;
      font-size: 0.9rem;
      height: 48px;
      border-radius: 8px;
      transition: all 0.2s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      width: 100%;

      &:active {
        transform: translateY(1px);
      }

      .me-1 {
        margin-right: 8px;
      }
    }
  }

  &.expanded {
    .action-content {
      animation: slideUp 0.3s ease-out forwards;
    }
  }
}

// Animation for sliding up
@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

// Cooking instruction list improvements
.cooking-instruction-list {
  list-style: none;
  margin-top: 8px;
  padding-left: 12px;
  border-left: 2px solid rgba(0,0,0,0.1);

  .cooking-instruction-list-item {
    font-size: 0.85rem;
    color: var(--text-secondary);
    margin-bottom: 6px;
    padding: 4px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Modal improvements
.modal-content {
  border-radius: 12px;
  border: var(--card-border);
  box-shadow: var(--card-shadow);
  background: var(--card-bg);

  .modal-header {
    border-bottom: var(--card-border);
    padding: 16px 20px;
    background: var(--card-bg);

    .modal-title {
      font-size: 1.2rem;
      color: var(--text-primary);
    }
  }

  .modal-body {
    padding: 20px;
    background: var(--bg-primary);
  }

  .modal-footer {
    border-top: var(--card-border);
    padding: 16px 20px;
    background: var(--card-bg);
  }
}

.selection-checkbox {
  .form-check-input {
    width: 18px;
    height: 18px;
    cursor: pointer;

    &:checked {
      background: var(--accent-electric-blue);
      border-color: var(--accent-electric-blue);
    }
  }
}

.selected-items-summary {
  background: var(--bg-primary);
  border-radius: 8px;
  padding: 12px;

  h6 {
    color: var(--text-primary);
    font-size: 0.9rem;
  }

  .selected-items-list {
    .selected-item {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      font-size: 0.85rem;

      .item-price {
        font-weight: 600;
        color: var(--accent-electric-blue);
      }
    }

    .more-items {
      text-align: center;
      font-size: 0.8rem;
      color: var(--text-secondary);
      padding-top: 8px;
    }
  }
}

// General button improvements
.btn {
  font-weight: 600;
  letter-spacing: 0.3px;
  border: none;

  &-primary {
    background: var(--accent-electric-blue);
    &:hover {
      background: var(--accent-electric-blue-dark);
    }
  }

  &-danger {
    background: var(--danger);
    &:hover {
      background: #d32f2f;
    }
  }

  &-info {
    background: var(--primary-button-bg);
    color: var(--primary-button-text);
    &:hover {
      background: var(--primary-button-hover);
      color: var(--primary-button-text);
    }
  }

  &-secondary {
    background: var(--secondary-button-bg);
    color: var(--secondary-button-text);
    border: 1px solid var(--card-border);
    &:hover {
      background: var(--secondary-button-bg-dark);
      color: var(--secondary-button-text);
    }
  }

  &-outline-primary {
    border: 1px solid var(--accent-electric-blue);
    color: var(--accent-electric-blue);
    background: transparent;
    &:hover {
      background: rgba(30, 136, 229, 0.14);
      color: var(--accent-electric-blue);
    }
  }

  &-outline-secondary {
    border: 1px solid var(--card-border);
    color: var(--text-secondary);
    background: transparent;
    &:hover {
      background: var(--secondary-button-bg);
      color: var(--text-primary);
    }
  }
}

.tip-badge {
  background: var(--accent-electric-blue);
  color: var(--dark-text-primary);
}
</style>
